import { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Footer } from "./Footer";
import { NotificationPanel } from "./NotificationPanel";
import { OnboardingModal } from "./OnboardingModal";
import {
  LayoutDashboard,
  Store,
  CandlestickChart,
  Sprout,
  GraduationCap,
  User,
  Bell,
  Search,
  HelpCircle,
  ChevronDown,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/hooks/useAuth";
import { UserAvatar } from "@/components/UserAvatar";
import { useNotifications } from "@/hooks/useNotifications";
import { useExchangeRate } from "@/hooks/useExchangeRate";
import { formatMoneyCdf } from "@/lib/utils";
import { OfflineBanner } from "@/components/OfflineBanner";
import { PushPermissionBanner } from "@/components/PushPermissionBanner";
import logoMombongo from "@/assets/logo-mombongo.png";

/* Items that map 1:1 to the mobile bottom nav, plus Financement & Wallet */
const mainNav = [
  {
    to: "/home",
    icon: LayoutDashboard,
    label: { fr: "Tableau de bord", en: "Dashboard", ln: "Etando" },
  },
  {
    to: "/market",
    icon: Store,
    label: { fr: "Marché", en: "Market", ln: "Libanda" },
  },
  {
    to: "/bourse",
    icon: CandlestickChart,
    label: { fr: "Bourse", en: "Exchange", ln: "Bourse" },
  },
  {
    to: "/financement",
    icon: Sprout,
    label: { fr: "Financement", en: "Financing", ln: "Lisalisi" },
  },
  {
    to: "/academia",
    icon: GraduationCap,
    label: { fr: "Academia", en: "Academy", ln: "Bino" },
  },
] as const;

const secondaryNav = [
  {
    to: "/profile",
    icon: User,
    label: { fr: "Mon profil", en: "My profile", ln: "Profil" },
  },
] as const;

export function DesktopShell({ children }: { children: React.ReactNode }) {
  const { lang, role } = useApp();
  const { user, userProfile } = useAuth();
  const { t } = useTranslation();
  const displayName = user?.displayName || userProfile?.displayName || t("common.unknown");
  const location = useLocation();
  const navigate = useNavigate();
  const { data: notifData } = useNotifications();
  const unread = notifData?.unreadCount ?? 0;
  const { data: rateData } = useExchangeRate();
  const rate = rateData?.rate ?? 2800;
  const [notifOpen, setNotifOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-app">
      {/* ============ SIDEBAR ============ */}
      <aside className="w-64 bg-white border-r border-gray-200 flex flex-col sticky top-0 h-screen overflow-y-auto">
        {/* Brand */}
        <button
          onClick={() => navigate("/home")}
          className="px-5 pt-6 pb-5 hover:bg-gray-50 transition text-left"
        >
          <img
            src={logoMombongo}
            alt="Mombongo Coopérative"
            className="h-10 w-auto object-contain"
          />
        </button>

        <div className="px-3 mt-2">
          <p className="px-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
            {t("sidebar.menu")}
          </p>
          <nav className="space-y-0.5">
            {mainNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-display font-semibold transition group ${
                    isActive
                      ? "bg-green-700 text-white shadow-elevated"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <n.icon
                      className="w-[18px] h-[18px]"
                      strokeWidth={isActive ? 2.4 : 2}
                    />
                    <span className="flex-1">{n.label[lang]}</span>
                    {n.to === "/bourse" && (
                      <span
                        className={`text-[9px] font-extrabold rounded-full px-1.5 py-0.5 ${isActive ? "bg-amber-400 text-amber-900" : "bg-amber-100 text-amber-700"}`}
                      >
                        LIVE
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <p className="px-3 mt-6 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
            {t("sidebar.account")}
          </p>
          <nav className="space-y-0.5">
            {secondaryNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13.5px] font-display font-semibold transition ${
                    isActive
                      ? "bg-green-50 text-green-700"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  }`
                }
              >
                <n.icon className="w-[18px] h-[18px]" strokeWidth={2} />
                <span className="flex-1">{n.label[lang]}</span>
              </NavLink>
            ))}
          </nav>
        </div>

        {/* User card pinned bottom */}
        <div className="mt-auto p-3">
          <button
            onClick={() => navigate("/profile")}
            className="w-full text-left"
          >
          <div className="bg-gradient-to-br from-green-700 to-green-800 text-white rounded-2xl p-4 hover:from-green-600 hover:to-green-700 transition">
            <div className="flex items-center gap-2.5">
              <UserAvatar
                photoURL={user?.photoURL}
                displayName={displayName}
                className="w-10 h-10 rounded-xl text-[14px]"
              />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-bold truncate">
                  {displayName}
                </p>
                <p className="text-[10px] text-white/60 capitalize">
                  {t(`role.${role}`)}
                </p>
              </div>
              <ChevronDown className="w-4 h-4 text-white/60" />
            </div>
            <div className="mt-3 pt-3 border-t border-white/15">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-white/70 font-semibold uppercase tracking-wider">
                  {t("profile.walletSection")}
                </span>
                <span className="font-display font-black text-[15px] tabular">
                  ${(userProfile?.walletUsd ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <p className="text-right text-[10px] text-amber-300 font-semibold tabular mt-0.5">
                {formatMoneyCdf(userProfile?.walletUsd ?? 0, rate)}
              </p>
            </div>
          </div>
          </button>
        </div>
      </aside>

      {/* ============ MAIN ============ */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-app/80 backdrop-blur-xl border-b border-gray-200/60">
          <div className="px-8 h-16 flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                placeholder="Rechercher cultures, agriculteurs, opportunités…"
                className="w-full h-10 bg-white border border-gray-200 rounded-xl pl-10 pr-4 text-[13px] focus:outline-none focus:ring-2 focus:ring-green-700/15 focus:border-green-700"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/aide')}
                className="h-10 px-3 rounded-xl bg-white border border-gray-200 hover:border-gray-300 transition flex items-center gap-2 text-[12px] font-semibold text-gray-700"
              >
                <HelpCircle className="w-4 h-4 text-gray-500" />
                Aide
              </button>
              <button
                onClick={() => setNotifOpen(true)}
                className="relative h-10 w-10 rounded-xl bg-white border border-gray-200 hover:border-gray-300 transition flex items-center justify-center"
              >
                <Bell className="w-4 h-4 text-gray-700" strokeWidth={2.2} />
                {unread > 0 && (
                  <span className="absolute top-2 right-2 w-2 h-2 bg-danger rounded-full ring-2 ring-app" />
                )}
              </button>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-x-hidden flex flex-col">
          <OfflineBanner />
          <PushPermissionBanner />
          <div className="flex-1 px-8 py-6 max-w-[1400px] mx-auto w-full">
            {children}
          </div>
          <Footer />
        </main>
      </div>

      <NotificationPanel open={notifOpen} onClose={() => setNotifOpen(false)} />
      <OnboardingModal />
    </div>
  );
}
